import _ from 'lodash';
import moment from 'moment';

import { CHARTCOLORS as COLORS } from 'src/constants/Colors';
import * as Urls from 'src/constants/EndpointUrls';

const DATE_FORMAT = 'MMMM YYYY';
export const PARSE_DATE_FORMAT = 'YYYY/MM';

export const sortUrl = {
  employmentType: `&order=asc&sort=employmentTypeId`,
  group: `&order=asc&sort=groupId`,
  occupation: `&sort=occupation`,
  position: `&order=asc&sort=positionId`
};

export const employeeCategoryLinkURL = id => ({
  employmentType: `&employment_type_ids=${id}`,
  group: `&group_ids=${id}`,
  occupation: `&occupation_ids=${id}`,
  payrollRulesGroup: `&payroll_rule_group=${id}`,
  position: `&position_ids=${id}`
});

const parseDate = date => moment(date, PARSE_DATE_FORMAT);

const getMonthFormat = language => (language === 'vi' ? 'MM' : 'MMM');

const calculateExpenseSummary = (data, year) => {
  const groupedByName = _.groupBy(data.flatMap(monthData => monthData.data), 'name');
  const totalExpensesByName = _.mapValues(groupedByName, items => _.sumBy(items, 'expenses'));
  return { ...totalExpensesByName, year };
};

export function generateColor(payrollRulesGroups = []) {
  const data = {};

  const mergedData = _.flatMap(payrollRulesGroups);
  const names = _.map(mergedData, 'name');
  const uniquePayrollRulesGroupsName = _.uniq(names);
  uniquePayrollRulesGroupsName.sort().forEach((name, index) => {
    data[`${name}`] = COLORS[index];
  });

  return data;
}

export function computeYearlyExpense(data = [], selectedYear) {
  const yearlyFrom = parseInt(selectedYear.compareYear, 10);
  const yearlyTo = parseInt(selectedYear.targetYear, 10);
  const expensesArray = [];
  for (let i = yearlyFrom; i <= yearlyTo; i += 1) {
    const index = data.findIndex(e => e.year === i);
    const expenseSummary = calculateExpenseSummary(data[index].data, i);
    expensesArray.push(expenseSummary);
  }
  return expensesArray;
}

export function payLoad(chartColors, reverse) {
  const pairs = _.toPairs(chartColors);
  const processedPairs = reverse ? pairs.reverse() : pairs;
  return processedPairs.map(([dataKey, color]) => ({ dataKey, color }));
}

export function getYearListFromData(data) {
  return data
    .filter(eachYear => eachYear.year !== undefined && eachYear.data !== undefined)
    .map(eachYear => eachYear.year)
    .sort();
}

export function getSalaryRulesListEachYear(data) {
  const obj = {};
  data.forEach(({ data: months, year }) => {
    if (months && year) {
      obj[year] = [];
      months.forEach(({ data: payrollRules }) => {
        payrollRules.forEach(({ rulesGroupId, name }) => {
          const existingPayrollRuleGroup = !obj[year].some(group => group.rulesGroupId === rulesGroupId);
          if (rulesGroupId !== undefined && name !== undefined && existingPayrollRuleGroup) {
            obj[year].push({ name, rulesGroupId });
          }
        });
      });
    }
  });
  return obj;
}
export function getSalaryRulesListInYearRange(listEachYear = {}, yearlyTo, yearlyFrom) {
  const list = [];
  const groupIds = new Set();
  for (let i = yearlyTo; i <= yearlyFrom; i += 1) {
    const yearData = listEachYear[`${i}`];
    if (yearData) {
      yearData.forEach(item => {
        const { name, rulesGroupId } = item;
        if (rulesGroupId !== undefined && name !== undefined && !groupIds.has(rulesGroupId)) {
          list.push(item);
          groupIds.add(rulesGroupId);
        }
      });
    }
  }
  return list;
}

export function getDataForGenerateInChart(data = []) {
  const convertDataToArray = _.isArray(data) ? data : [data];

  const monthFormat = getMonthFormat(moment.locale());
  return convertDataToArray.reduce((allData, { data: months, year }) => {
    if (!months && !year) return allData;
    const dataEachYear = months.map(({ month, data: payrollRulesGroups }) => {
      const dataEachMonth = {
        month: moment(month, 'M').format(monthFormat),
        monthNumber: month
      };
      payrollRulesGroups.forEach(({ name, expenses }) => {
        if (name && expenses) dataEachMonth[name] = expenses;
      });
      return dataEachMonth;
    });
    allData.push({ year, data: dataEachYear.reverse() });
    return allData;
  }, []);
}

export const generateExpensesSummaryChartData = (expensesSummary, selectedYear, viewMode = 'Monthly') => {
  const salaryRulesListForEachYear = getSalaryRulesListEachYear(expensesSummary);
  const colors = generateColor(salaryRulesListForEachYear);

  const chartData = viewMode === 'Monthly' ? getDataForGenerateInChart(expensesSummary)[0] : { data: computeYearlyExpense(expensesSummary, selectedYear) };
  return { ...chartData, colors };
};

export const getMonthFullName = label => {
  const fullName = {
    Jan: 'January',
    Feb: 'February',
    Mar: 'March',
    Apr: 'April',
    May: 'May',
    Jun: 'June',
    Jul: 'July',
    Aug: 'August',
    Sep: 'September',
    Oct: 'October',
    Nov: 'November',
    Dec: 'December',
    'ม.ค.': 'มกราคม',
    'ก.พ.': 'กุมภาพันธ์',
    'มี.ค.': 'มีนาคม',
    'เม.ย.': 'เมษายน',
    'พ.ค.': 'พฤษภาคม',
    'มิ.ย.': 'มิถุนายน',
    'ก.ค.': 'กรกฎาคม',
    'ส.ค.': 'สิงหาคม',
    'ก.ย.': 'กันยายน',
    'ต.ค.': 'ตุลาคม',
    'พ.ย.': 'พฤศจิกายน',
    'ธ.ค.': 'ธันวาคม'
  };
  return fullName[label] || label;
};

export const sumExpense = (payload = []) => {
  if (!_.isArray(payload)) return 0;
  const invalidEntry = payload.find(data => !_.isNumber(data.value));
  if (invalidEntry) {
    return 0;
  }
  return _.sumBy(payload, 'value');
};

/**
 * Converts a numeric string into a formatted currency string.
 *
 * @param {string} numericString - The numeric string to be converted.
 * @returns {string} The formatted currency string with two decimal places.
 */
export const parseNumberStringToCurrency = numericString => {
  const numericValue = parseFloat(numericString);
  const formatter = new Intl.NumberFormat('vi', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return formatter.format(numericValue);
};

/**
 * Updates the language of month names in chart data based on the provided i18n language setting.
 *
 * @param {Object} chartData - The chart data object containing a 'data' array with month numbers.
 * @param {Object} i18n - The internationalization object with a 'language' property.
 * @returns {Object} - The updated chart data with month names formatted according to the specified language.
 */
export const changeChartLanguage = (chartData = {}, i18n) => {
  moment.locale(i18n.language);
  const monthFormat = getMonthFormat(i18n.language);
  const data = _.get(chartData, 'data', []);
  const newData = data.map(({ monthNumber, ...rest }) => ({
    ...rest,
    month: moment(monthNumber, 'M').format(monthFormat),
    monthNumber
  }));
  return { ...chartData, data: newData };
};

/**
 * Adjusts the comparison year in the parameters if the view mode is 'Yearly'
 * and the target year is less than the comparison year.
 *
 * @param {Object} params - The parameters containing target and comparison years.
 * @param {string} viewMode - The current view mode, expected to be 'Yearly'.
 * @returns {Object} The updated parameters with the adjusted comparison year if needed.
 */
export const checkCompareYear = (params, viewMode) => {
  if (viewMode === 'Yearly' && parseInt(params.target_year, 10) < parseInt(params.compare_year, 10)) {
    return { ...params, compare_year: params.target_year };
  }
  return params;
};

/**
 * Converts a month number to a formatted string representation.
 * Returns 'ThisMonth' if the number matches current month,
 * otherwise returns month name prefixed with 'On' (e.g. 'OnJanuary').
 * @param {number} number - Month number (1-12)
 * @returns {string} Formatted month string
 */
export const convertMonthNumberToStr = number => {
  moment.locale('en');
  const numberOfThisMonth = moment().month() + 1;
  if (number === numberOfThisMonth) return 'ThisMonth';
  return `On${moment(number, 'M').format('MMMM')}`;
};

/**
 * Generates new chart colors based on the input data.
 *
 * @param {Array} chartData - The data to generate chart colors for.
 * @returns {Array} An array of objects with updated colors.
 */
export const generateChartColor = chartData =>
  chartData.map((item, index) => ({
    ...item,
    color: COLORS[index % COLORS.length]
  }));

/**
 * Constructs a URL for confirming payroll rules with query parameters.
 *
 * @param {Object} confirmQuery - An object containing the rules group ID.
 * @param {string} paymentDate - The date of the payment in a format recognized by moment.js.
 * @returns {string} A URL string with appended query parameters for payroll confirmation.
 */
export const linkToConfirmWithQuery = (confirmQuery, paymentDate) => {
  const date = moment(paymentDate).format('YYYY_MM_DD');
  const query = `payroll_rules_groups=${confirmQuery.rulesGroupId}&year_month=${date}`;
  return `${Urls.EMPLOYEE_PAY_LIST}?${query}`;
};

/**
 * Calculates the total sum of expenses from the provided data, excluding any items
 * with IDs specified in the disabled list. The result is formatted as a currency string.
 *
 * @param {Array} expensesData - An array of expense objects, each containing an 'id' and 'number'.
 * @param {Array} [disabled=[]] - An optional array of IDs to exclude from the sum calculation.
 * @returns {string} - The total sum of expenses formatted as a currency string.
 */
export const calcSumExpensesData = (expensesData, disabled = []) => {
  if (!expensesData || expensesData.length === 0) return 0;
  const totalExpenses = expensesData.reduce((acc, item) => {
    if (!disabled.includes(item.id.toString())) return acc + item.number;
    return acc;
  }, 0);
  return parseNumberStringToCurrency(totalExpenses);
};

/**
 * Filters and processes expense data for a given month and year.
 *
 * @param {Object} selectedDate - The selected date object containing expense data.
 * @returns {Object} An object containing filtered expense data, total sum of expenses,
 *                   confirmation status, list of unconfirmed items, and query details.
 */
export const filterExpensesData = selectedDate => {
  if (!selectedDate?.data || selectedDate?.data.length === 0) return selectedDate;

  const output = {
    data: [],
    sumExpensesData: 0,
    isConfirmed: true,
    unConfirmedList: [],
    confirmedQuery: { rulesGroupId: 1, paymentDate: 1 },
    isFirst: selectedDate.isFirst,
    isLatest: selectedDate.isLatest,
    date: selectedDate.date
  };

  const unConfirmed = [];
  const filtered0ExpenseMonth = selectedDate.data.filter(item => Number(item.expenses) !== 0);

  if (filtered0ExpenseMonth.length === 0) {
    return { ...output, isConfirmed: false };
  }

  output.data = filtered0ExpenseMonth.map((item, index) => {
    const color = COLORS[index % COLORS.length];
    output.confirmedQuery = { rulesGroupId: item.rulesGroupId, paymentDate: item.paymentDate };
    if (!item.isConfirmed) {
      output.isConfirmed = false;
      unConfirmed.push(item.name);
    }
    return {
      ...item,
      color,
      id: index,
      number: parseFloat(item.expenses),
      link: linkToConfirmWithQuery(output.confirmedQuery, item.paymentDate)
    };
  });

  output.sumExpensesData = calcSumExpensesData(output.data);
  output.unConfirmedList = unConfirmed;
  return output;
};

/**
 * Determines if the given date is within the current month.
 *
 * @param {string} date - The date to be checked, in a parsable string format.
 * @param {moment.Moment} currentMonth - A moment object representing the current month.
 * @returns {boolean} True if the date is within the current month, otherwise false.
 */
export const isSelectingCurrentMonth = (date, currentMonth) => {
  const selectedDate = parseDate(date);
  return selectedDate.isSame(currentMonth);
};

/**
 * Converts a selected month into a human-readable string indicating its
 * relation to the current month.
 *
 * @param {string} selectedMonth - The month to be converted, in a parsable date format.
 * @param {moment.Moment} currentMonth - The current month as a moment object.
 * @param {Function} t - Translation function for localizing strings.
 * @returns {string} - A localized string indicating whether the selected month
 *                     is the current month, a future month, or how many months
 *                     ago it was.
 */
export const monthConverter = (selectedMonth, currentMonth, t) => {
  const date = parseDate(selectedMonth);
  const monthDiff = date.diff(currentMonth, 'months');
  const absMonthDiff = Math.abs(monthDiff);

  if (monthDiff === 0) {
    return t('utils.Dashboard.this_month');
  } else if (monthDiff > 0) {
    return t('utils.Dashboard.next_month');
  } else if (monthDiff === -1) {
    return t('utils.Dashboard.last_month');
  }

  return `${absMonthDiff} ${t('utils.Dashboard.months_ago')}`;
};

/**
 * Formats a given date into a month-year string based on the specified locale.
 *
 * @param {Date|string} date - The date to be formatted. Can be a Date object or a string.
 * @param {Object} i18n - The internationalization object containing the language setting.
 * @returns {string} The formatted date string in the specified month-year format.
 */
export const monthYearFormat = (date, i18n) => {
  if (!date) return '';
  moment.locale(i18n.language);
  return parseDate(date).format(DATE_FORMAT);
};
